/**
 * Renders the report header (logo + 4 text elements + study grid) above the editor.
 * Config mirrors headers.html reference: each of Hospital Name, Address, Department,
 * Title is independently stylable (size / weight / color / font / visibility).
 */
import styles from './HeaderView.module.css';

export type TextWeight = 'normal' | 'bold' | 'black';
export const FONT_FAMILIES = [
  { value: 'system-ui, -apple-system, sans-serif', label: 'System Default' },
  { value: '"Segoe UI", sans-serif', label: 'Segoe UI' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'Impact, sans-serif', label: 'Impact' },
  { value: '"Comic Sans MS", cursive', label: 'Comic Sans MS' },
  { value: 'ui-sans-serif, sans-serif', label: 'System UI' },
];

export interface TextElement {
  text?: string;
  sizePt?: number;
  weight?: TextWeight;
  color?: string;
  fontFamily?: string;
  visible?: boolean;
}

export interface LogoElement {
  dataUri?: string | null;
  maxWidth?: number;
  maxHeight?: number;
  position?: 'above' | 'left' | 'right';
  visible?: boolean;
}

export interface HeaderConfig {
  logo?: LogoElement;
  elements?: {
    hospitalName?: TextElement;
    address?: TextElement;
    department?: TextElement;
    title?: TextElement;
  };
  grid?: HeaderGridRow[];

  // --- legacy shape (pre-rich model) — read-only, migrated on load ---
  center?: {
    clinicName?: string;
    line1?: string;
    line2?: string;
    title?: string;
    logoPos?: 'left' | 'right' | 'above';
  };
}

export interface HeaderGridRow {
  cells: HeaderGridCell[];
}

/** A cell is a list of segments. Each segment is either literal text or a
 *  placeholder that resolves against the current study.
 *  Enables cells like `[Age: ][{patientAge}][ / ][{patientSex}]` or the
 *  simpler `[Modality: ][{modality}]` or bare `[Some literal]`. */
export type CellSegment =
  | { kind: 'text'; text: string }
  | { kind: 'field'; fieldId: string };

export interface HeaderGridCell {
  colSpan?: number;
  /** New segment-based content. If present, renders these in order. */
  segments?: CellSegment[];
  /** Per-cell text formatting - applies to every segment in this cell.
   *  Defaults to 10pt Arial normal black (matches insertHeader's SFDT
   *  default) when unset. Kept optional so old saves render unchanged. */
  fontFamily?: string;
  sizePt?: number;
  weight?: TextWeight;
  color?: string;
  /** Legacy shape (single-field + optional label). Kept for backward compat
   *  with existing saved configs; migrated to `segments` on read via
   *  `cellToSegments`. New saves emit `segments` and leave these unset. */
  fieldId?: string;
  label?: string;
}

export interface StudyFields {
  patientName?: string;
  patientId?: string;
  patientDob?: string;
  patientSex?: string;
  patientAge?: string;
  accessionNumber?: string;
  studyDate?: string;
  studyDescription?: string;
  modality?: string;
  referringPhysician?: string;
  institution?: string;
  [k: string]: string | undefined;
}

const FIELD_LABELS: Record<string, string> = {
  patientName: 'Patient Name',
  patientId: 'Patient ID',
  patientDob: 'DOB',
  patientSex: 'Sex',
  patientAge: 'Age',
  accessionNumber: 'Accession #',
  studyDate: 'Study Date',
  studyDescription: 'Description',
  modality: 'Modality',
  referringPhysician: 'Ref. Physician',
  institution: 'Institution',
};

function fieldLabel(fieldId: string, override?: string): string {
  return override || FIELD_LABELS[fieldId] || fieldId;
}

/** DICOM Person Name (PN) VR format is `Last^First^Middle^Prefix^Suffix`
 *  with `^` separating name components and (optionally) `=` separating
 *  alphabetic / ideographic / phonetic renditions. Radiologists don't
 *  want to see the caret. Strip it out, keep only the alphabetic
 *  rendition (before `=`), and collapse whitespace so we get "Doe John M"
 *  from "Doe^John^M". */
export function formatDicomPersonName(raw: string): string {
  if (!raw) return '';
  return raw
    .split('=')[0]           // just the alphabetic rendition
    .split('^')              // component parts
    .map((s) => s.trim())    // drop leading/trailing spaces
    .filter(Boolean)         // skip empty components
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function fieldValue(study: StudyFields, fieldId: string): string {
  const raw = (study[fieldId] as string) || '';
  if (!raw) return '—';
  // Any PN-typed field goes through the DICOM formatter so the ^ is
  // replaced with spaces. patientName is the common one; add others if
  // they turn out to also be PN VR (referring physician sometimes is).
  if (fieldId === 'patientName' || fieldId === 'referringPhysician') {
    return formatDicomPersonName(raw);
  }
  return raw;
}

/** Return the effective segments for a cell. Falls back to the legacy
 *  `{fieldId, label}` shape - old saves used a single field + optional label
 *  and were always rendered as `Label: Value`. We reproduce that by
 *  emitting `[text: "Label: "][field: fieldId]`. */
export function cellToSegments(cell: HeaderGridCell): CellSegment[] {
  if (cell.segments && cell.segments.length > 0) return cell.segments;
  if (cell.fieldId) {
    const lbl = fieldLabel(cell.fieldId, cell.label);
    const out: CellSegment[] = [];
    if (lbl) out.push({ kind: 'text', text: `${lbl}: ` });
    out.push({ kind: 'field', fieldId: cell.fieldId });
    return out;
  }
  return [];
}

/** Flatten a cell's segments into a single string for the given study.
 *  Used by insertHeader for the SFDT grid, where each cell is one text run. */
export function cellToString(cell: HeaderGridCell, study: StudyFields): string {
  return cellToSegments(cell).map((seg) => {
    if (seg.kind === 'text') return seg.text;
    return fieldValue(study, seg.fieldId);
  }).join('');
}

function weightToCss(w?: TextWeight): number {
  if (w === 'black') return 900;
  if (w === 'bold') return 700;
  return 400;
}

function textStyle(el?: TextElement): React.CSSProperties {
  if (!el) return {};
  return {
    fontSize: `${el.sizePt ?? 12}pt`,
    fontWeight: weightToCss(el.weight),
    color: el.color || '#000',
    fontFamily: el.fontFamily || 'system-ui, -apple-system, sans-serif',
    display: el.visible === false ? 'none' : undefined,
  };
}

/** Upgrade old flat-center shape to the rich model so existing saves still render. */
export function migrateHeaderConfig(cfg: HeaderConfig | null | undefined): HeaderConfig {
  if (!cfg) return DEFAULT_HEADER_CONFIG;
  // Already rich?
  if (cfg.elements || cfg.logo?.dataUri !== undefined) return cfg;

  // Legacy migration
  const out: HeaderConfig = {
    logo: {
      dataUri: (cfg as any).logo ?? null,
      maxWidth: 160,
      maxHeight: 56,
      position: cfg.center?.logoPos || 'left',
      visible: true,
    },
    elements: {
      hospitalName: {
        text: cfg.center?.clinicName || DEFAULT_HEADER_CONFIG.elements!.hospitalName!.text,
        sizePt: 16, weight: 'black', color: '#000080',
        fontFamily: FONT_FAMILIES[0].value, visible: true,
      },
      address: {
        text: [cfg.center?.line1, cfg.center?.line2].filter(Boolean).join(' · ') ||
              DEFAULT_HEADER_CONFIG.elements!.address!.text,
        sizePt: 8, weight: 'normal', color: '#333333',
        fontFamily: FONT_FAMILIES[0].value, visible: true,
      },
      department: {
        text: DEFAULT_HEADER_CONFIG.elements!.department!.text,
        sizePt: 10, weight: 'bold', color: '#000080',
        fontFamily: FONT_FAMILIES[0].value, visible: true,
      },
      title: {
        text: cfg.center?.title || DEFAULT_HEADER_CONFIG.elements!.title!.text,
        sizePt: 12, weight: 'black', color: '#800000',
        fontFamily: FONT_FAMILIES[0].value, visible: true,
      },
    },
    grid: cfg.grid,
  };
  return out;
}

interface Props {
  config: HeaderConfig | null;
  study: StudyFields;
}

export default function HeaderView({ config: rawConfig, study }: Props) {
  if (!rawConfig) return null;
  const config = migrateHeaderConfig(rawConfig);

  const logo = config.logo || {};
  const els = config.elements || {};
  const logoVisible = logo.visible !== false && !!logo.dataUri;
  const logoPos = logo.position || 'left';

  const renderLogo = () => logoVisible && (
    <img
      src={logo.dataUri as string}
      alt="Logo"
      className={styles.logo}
      style={{
        maxWidth: logo.maxWidth ? `${logo.maxWidth}px` : undefined,
        maxHeight: logo.maxHeight ? `${logo.maxHeight}px` : undefined,
      }}
    />
  );

  const renderText = (el?: TextElement) =>
    el && el.text && el.visible !== false && (
      <div style={textStyle(el)}>{el.text}</div>
    );

  const textGroup = (
    <div className={styles.textGroup}>
      {renderText(els.hospitalName)}
      {renderText(els.address)}
      {renderText(els.department)}
      {renderText(els.title)}
    </div>
  );

  return (
    <div className={styles.headerWrap}>
      <div className={`${styles.center} ${styles['logoPos_' + logoPos]}`}>
        {logoPos !== 'right' && renderLogo()}
        {textGroup}
        {logoPos === 'right' && renderLogo()}
      </div>

      {config.grid && config.grid.length > 0 && (
        <table className={styles.grid}>
          <tbody>
            {config.grid.map((row, ri) => (
              <tr key={ri}>
                {row.cells.map((cell, ci) => {
                  const segments = cellToSegments(cell);
                  const cellStyle: React.CSSProperties = {
                    fontFamily: cell.fontFamily || undefined,
                    fontSize: cell.sizePt ? `${cell.sizePt}pt` : undefined,
                    fontWeight: cell.weight ? weightToCss(cell.weight) : undefined,
                    color: cell.color || undefined,
                  };
                  // When the user picks a colour, force it onto every child
                  // span so it overrides .gridLabel / .gridValue's built-in
                  // colours (label blue, value dark). Unset = classes decide.
                  const inlineColor: React.CSSProperties | undefined =
                    cell.color ? { color: cell.color } : undefined;
                  return (
                    <td key={ci} colSpan={cell.colSpan || 1} style={cellStyle}>
                      {segments.map((seg, si) => seg.kind === 'text' ? (
                        <span key={si} className={styles.gridLabel} style={inlineColor}>{seg.text}</span>
                      ) : (
                        <span key={si} className={styles.gridValue} style={inlineColor}>{fieldValue(study, seg.fieldId)}</span>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Sensible defaults, matching the reference's values. */
export const DEFAULT_HEADER_CONFIG: HeaderConfig = {
  logo: {
    dataUri: null,
    maxWidth: 150,
    maxHeight: 50,
    position: 'left',
    visible: true,
  },
  elements: {
    hospitalName: {
      text: 'MEDICAL CENTER HOSPITAL',
      sizePt: 16, weight: 'black', color: '#000080',
      fontFamily: FONT_FAMILIES[0].value, visible: true,
    },
    address: {
      text: '123 Medical Drive, City, State 12345',
      sizePt: 8, weight: 'normal', color: '#333333',
      fontFamily: FONT_FAMILIES[0].value, visible: true,
    },
    department: {
      text: 'RADIOLOGY DEPARTMENT',
      sizePt: 10, weight: 'bold', color: '#000080',
      fontFamily: FONT_FAMILIES[0].value, visible: true,
    },
    title: {
      text: 'IMAGING REPORT',
      sizePt: 12, weight: 'black', color: '#800000',
      fontFamily: FONT_FAMILIES[0].value, visible: true,
    },
  },
  grid: [
    { cells: [
      { fieldId: 'patientName', colSpan: 2 },
      { fieldId: 'patientId' },
      { fieldId: 'patientSex' },
    ] },
    { cells: [
      { fieldId: 'patientAge' },
      { fieldId: 'patientDob' },
      { fieldId: 'studyDate' },
      { fieldId: 'accessionNumber' },
    ] },
    { cells: [
      { fieldId: 'modality' },
      { fieldId: 'studyDescription', colSpan: 2 },
      { fieldId: 'referringPhysician' },
    ] },
  ],
};
